import type { Invoice } from '@/shared/domain'
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/primitives'
import { HelpTooltip } from './help-tooltip'
import { SortableTableHead } from './sortable-table-head'
import { PaymentRow, type PaymentRowActionHandlers } from './payment-row'

export type PaymentSortColumn = 'date' | 'amount' | 'status' | 'dueDate'
export type PaymentSortableColumn = 'amount' | 'status' | 'dueDate'
export type PaymentSortOrder = 'asc' | 'desc'

export type PaymentRegisterTableProps = {
  invoices: Invoice[]
  sortBy: PaymentSortColumn
  sortOrder: PaymentSortOrder
  onSortChange: (column: PaymentSortableColumn) => void
  expandedIds: Set<string>
  onToggleExpand: (id: string) => void
  highlightedId?: string | null
  actions: PaymentRowActionHandlers
}

export function PaymentRegisterTable({
  invoices,
  sortBy,
  sortOrder,
  onSortChange,
  expandedIds,
  onToggleExpand,
  highlightedId,
  actions,
}: PaymentRegisterTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>DATE</TableHead>
          <TableHead>CLIENT</TableHead>
          <TableHead>TYPE</TableHead>
          <TableHead>METHOD</TableHead>
          <SortableTableHead
            sortKey="amount"
            activeKey={sortBy}
            direction={sortOrder}
            onToggle={() => onSortChange('amount')}
          >
            AMOUNT
          </SortableTableHead>
          <SortableTableHead
            sortKey="dueDate"
            activeKey={sortBy}
            direction={sortOrder}
            onToggle={() => onSortChange('dueDate')}
            adornment={
              <HelpTooltip content="The date payment is expected. Deposit invoices are due before the hunt; final balances follow the outfitter's payment-timing settings." />
            }
          >
            DUE DATE
          </SortableTableHead>
          <SortableTableHead
            sortKey="status"
            activeKey={sortBy}
            direction={sortOrder}
            onToggle={() => onSortChange('status')}
            adornment={
              <HelpTooltip content='Invoice state — draft, sent, paid, overdue, or void. "Overdue" means the due date has passed while a balance is still owed.' />
            }
          >
            STATUS
          </SortableTableHead>
          <TableHead className="text-right">ACTIONS</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {invoices.map((invoice) => (
          <PaymentRow
            key={invoice.id}
            invoice={invoice}
            isExpanded={expandedIds.has(invoice.id)}
            onToggleExpand={onToggleExpand}
            isHighlighted={invoice.id === highlightedId}
            actions={actions}
          />
        ))}
      </TableBody>
    </Table>
  )
}
